import React from 'react';
import { CheckCircle, Clock, AlertCircle, XCircle, Loader } from 'lucide-react';

interface EntityBadgeProps {
    status: string;
    blockedReason?: string | null;
    confidence?: number | null;
    className?: string;
}

const EntityBadge: React.FC<EntityBadgeProps> = ({
    status,
    blockedReason,
    confidence,
    className = ''
}) => {
    const getBadgeConfig = () => {
        switch (status) {
            case 'READY':
                return {
                    icon: CheckCircle,
                    text: 'Spec Ready',
                    bgColor: 'bg-green-100',
                    textColor: 'text-green-800',
                    borderColor: 'border-green-300'
                };
            case 'QUEUED':
                return {
                    icon: Clock,
                    text: 'Analyzing...',
                    bgColor: 'bg-blue-100',
                    textColor: 'text-blue-800',
                    borderColor: 'border-blue-300',
                    animate: true
                };
            case 'BLOCKED':
                return {
                    icon: AlertCircle,
                    text: blockedReason ? `Blocked: ${formatReason(blockedReason)}` : 'Blocked',
                    bgColor: 'bg-yellow-100',
                    textColor: 'text-yellow-800',
                    borderColor: 'border-yellow-300'
                };
            case 'FAILED':
                return {
                    icon: XCircle,
                    text: 'Failed',
                    bgColor: 'bg-red-100',
                    textColor: 'text-red-800',
                    borderColor: 'border-red-300'
                };
            case 'PENDING':
            default:
                return {
                    icon: Loader,
                    text: 'Pending',
                    bgColor: 'bg-gray-100',
                    textColor: 'text-gray-800',
                    borderColor: 'border-gray-300'
                };
        }
    };

    const formatReason = (reason: string): string => {
        const formatted = reason.replace(/_/g, ' ');
        return formatted.charAt(0).toUpperCase() + formatted.slice(1);
    };

    const config = getBadgeConfig();
    const Icon = config.icon;

    return (
        <div className={`inline-flex items-center space-x-1.5 px-3 py-1.5 rounded-full border ${config.bgColor} ${config.textColor} ${config.borderColor} ${className}`}>
            <Icon className={`w-4 h-4 ${config.animate ? 'animate-pulse' : ''}`} />
            <span className="text-xs font-medium">{config.text}</span>
            {confidence !== null && confidence !== undefined && status === 'BLOCKED' && (
                <span className="text-xs opacity-75">
                    ({Math.round(confidence * 100)}% confidence)
                </span>
            )}
        </div>
    );
};

export default EntityBadge;
